import { format, isBefore, isToday, startOfDay } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { Card } from "@/components/card";
import { Button } from "@/components/ui/button";
import { SessionCard } from "@/features/session/components/session-card";
import { PublicSession } from "@classview/shared-types";

type SessionListProps = {
  sessions: PublicSession[];
  selectedDate: string;
};

type GroupedSessionsByDay = Map<string, PublicSession[]>;

function groupSessionsByDay(sessions: PublicSession[]): GroupedSessionsByDay {
  const grouped = new Map<string, PublicSession[]>();

  sessions.forEach((session) => {
    const sessionDate = new Date(session.start);
    const dateKey = format(sessionDate, "yyyy-MM-dd");

    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }

    grouped.get(dateKey)?.push(session);
  });

  // Sort sessions within each day by start time
  grouped.forEach((daySessions) => {
    daySessions.sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
    );
  });

  return grouped;
}

function getDayTitle(dateKey: string): string {
  const date = new Date(dateKey);
  if (isToday(date)) {
    return "Today";
  }
  return format(date, "dd/MM/yyyy");
}

function SessionGroup({
  title,
  sessions,
  dateKey,
}: {
  title: string;
  sessions: PublicSession[];
  dateKey: string;
}) {
  return (
    <div id={`day-${dateKey}`} className="relative scroll-mt-4 space-y-4">
      <div>
        <div className="absolute -left-6 top-1 inline-block rounded-full border border-secondary bg-white p-0.5 ring-8 ring-white dark:ring-background">
          <div className="size-2 rounded-full bg-secondary"></div>
        </div>
        <p className="text-sm font-semibold text-secondary">{title}</p>
        {sessions.length > 0 && (
          <p className="text-sm text-muted">
            {sessions.length} session{sessions.length === 1 ? "" : "s"}
          </p>
        )}
      </div>
      {sessions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8">
          <CalendarIcon className="mb-2 size-6 text-muted" />
          <p className="text-sm text-muted">No sessions scheduled</p>
        </div>
      ) : (
        sessions.map((session) => {
          const isEvent = !!session.eventId;

          return (
            <Card key={session.id} as="article">
              <Card.Content className="space-y-4">
                <SessionCard session={session} />
                <div className="flex items-center justify-end gap-2">
                  {!isEvent && (
                    <Button variant="outline" asChild>
                      <Link to={`/sessions/${session.id}`}>View</Link>
                    </Button>
                  )}
                  {isEvent ? (
                    <Button asChild>
                      <Link to={`/events/${session.eventId}`}>View Event</Link>
                    </Button>
                  ) : (
                    <Button asChild>
                      <Link to={`/r/${session.room.key}`} target="_blank">
                        Join Call
                      </Link>
                    </Button>
                  )}
                </div>
              </Card.Content>
            </Card>
          );
        })
      )}
    </div>
  );
}

export function SessionList(props: SessionListProps) {
  const { sessions, selectedDate } = props;

  const groupedSessions = groupSessionsByDay(sessions);

  const today = startOfDay(new Date());
  const selected = startOfDay(new Date(selectedDate));
  const todayKey = format(today, "yyyy-MM-dd");
  const selectedKey = format(selected, "yyyy-MM-dd");

  // Always ensure today is in the grouped sessions
  if (!groupedSessions.has(todayKey)) {
    groupedSessions.set(todayKey, []);
  }

  // Always ensure selected date is in the grouped sessions
  if (!groupedSessions.has(selectedKey)) {
    groupedSessions.set(selectedKey, []);
  }

  // Sort date keys: selected date first if it's in the past, otherwise today first, then chronologically
  const sortedDateKeys = Array.from(groupedSessions.keys()).sort((a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    const dateAStart = startOfDay(dateA);
    const dateBStart = startOfDay(dateB);

    // If selected date is in the past, it comes first
    if (isBefore(selected, today)) {
      if (dateAStart.getTime() === selected.getTime()) return -1;
      if (dateBStart.getTime() === selected.getTime()) return 1;
    }

    // Otherwise sort chronologically
    return dateA.getTime() - dateB.getTime();
  });

  return (
    <div className="space-y-8 border-l-2 border-border pl-4">
      {sortedDateKeys.map((dateKey) => (
        <SessionGroup
          key={dateKey}
          dateKey={dateKey}
          title={getDayTitle(dateKey)}
          sessions={groupedSessions.get(dateKey) || []}
        />
      ))}
    </div>
  );
}
